import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CalendarClock, CarFront, Stethoscope, FileText, Mail, Check, Clock, ChevronRight } from 'lucide-react';
import { ListScreen } from './ListScreen';
import { LANE_META, useFollowUps, type FollowUp, type FollowUpLane } from './useFollowUps';
import CustomerEmailModal from '../components/CustomerEmailModal';
import { useVertical } from '../../../../context/VerticalContext';

const LANE_ICON: Record<FollowUpLane, React.ElementType> = {
    mot: CalendarClock,
    visit: CarFront,
    advisory: Stethoscope,
    quote: FileText,
};

/**
 * Which email template opens for a given lane. The composer already has all of
 * these written — this is only about landing on the right one so the mechanic
 * is one tap from a sent message rather than picking from a gallery every time.
 */
const LANE_TEMPLATE: Record<FollowUpLane, string> = {
    mot: 'mot_reminder',
    visit: 'service_due',
    advisory: 'diagnostic_followup',
    quote: 'payment_reminder',
};

const VALID_LANES: FollowUpLane[] = ['mot', 'visit', 'advisory', 'quote'];

const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * How late something is, in words. "180 days overdue" is a number you have to
 * decode; "6 months late" is a fact you can act on.
 */
const lateness = (days: number): { text: string; tone: 'overdue' | 'due' | 'soon' } => {
    // "6mo late", not "6 months late": the badge is uppercase and tracked, and
    // at 320px the longer form took half the row and squeezed the customer's
    // name down to "Christo…". The name is the thing you are scanning for.
    if (days > 60) return { text: `${Math.round(days / 30)}mo late`, tone: 'overdue' };
    if (days > 0) return { text: `${days} ${days === 1 ? 'day' : 'days'} late`, tone: 'overdue' };
    if (days === 0) return { text: 'Due today', tone: 'due' };
    return { text: `In ${Math.abs(days)} ${Math.abs(days) === 1 ? 'day' : 'days'}`, tone: 'soon' };
};

export default function FollowUpsPage() {
    const { lane: laneParam } = useParams<{ lane: string }>();
    const navigate = useNavigate();
    const { activeOrgId } = useVertical();

    const lane = VALID_LANES.includes(laneParam as FollowUpLane) ? (laneParam as FollowUpLane) : null;
    const { items, loading, dismiss, refresh } = useFollowUps(lane);
    const [emailing, setEmailing] = useState<FollowUp | null>(null);

    if (!lane) {
        return (
            <div className="max-w-4xl mx-auto py-16 px-4 text-center">
                <p className="font-bold text-fg">That follow-up list doesn't exist.</p>
                <button onClick={() => navigate('/clients')} className="mt-3 text-sm font-bold text-accent hover:underline">
                    Back to clients
                </button>
            </div>
        );
    }

    const meta = LANE_META[lane];

    const handleDismiss = async (item: FollowUp, months?: number) => {
        const ok = await dismiss(item, months);
        if (!ok) {
            toast.error("Couldn't update that — try again.");
            return;
        }
        toast.success(months ? `Back in ${months} months` : 'Done');
    };

    return (
        <>
            <ListScreen<FollowUp>
                title={meta.label}
                subtitle={meta.blurb}
                icon={LANE_ICON[lane]}
                items={items}
                loading={loading}
                keyOf={(i) => `${i.source_id}:${i.item_key}`}
                searchText={(i) => [i.customer_name, i.vrm, i.vehicle_desc, i.headline].filter(Boolean).join(' ')}
                searchPlaceholder="Search name or registration…"
                emptyTitle="Nothing to chase"
                emptyBody={meta.empty}
                // The MOT lane is the worklist; MotRemindersPage is where the
                // automatic emails are switched on and off per vehicle. Two
                // different jobs, so the settings screen lives behind the list
                // rather than as a second row on the Clients page.
                children={lane === 'mot' ? (
                    <button
                        onClick={() => navigate('/reminders')}
                        className="w-full text-left text-xs text-accent hover:underline"
                    >
                        Manage automatic MOT reminder emails →
                    </button>
                ) : undefined}
                renderItem={(item) => {
                    const late = lateness(item.days_overdue);
                    return (
                        <div className="rounded-xl border border-line bg-panel p-4 space-y-3">

                            {/* Who + what */}
                            <div className="flex items-start justify-between gap-3">
                                <button
                                    onClick={() => item.customer_id && navigate(`/clients/${item.customer_id}`)}
                                    disabled={!item.customer_id}
                                    className="min-w-0 flex-1 text-left group disabled:cursor-default"
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        <span className="font-bold text-fg truncate group-enabled:group-hover:text-accent transition-colors">
                                            {item.customer_name}
                                        </span>
                                        {item.customer_id && (
                                            <ChevronRight className="w-4 h-4 text-fg-4 shrink-0" />
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                                        {item.vrm && (
                                            <span
                                                className="inline-block px-1.5 py-0.5 rounded-sm font-mono text-[11px] font-bold tracking-wider shrink-0"
                                                style={{ backgroundColor: 'var(--fd-plate)', color: 'var(--fd-plate-ink)' }}
                                            >
                                                {item.vrm}
                                            </span>
                                        )}
                                        {item.vehicle_desc && (
                                            <span className="text-xs text-fg-4 truncate">{item.vehicle_desc}</span>
                                        )}
                                    </div>
                                </button>

                                <span
                                    className={`shrink-0 text-fd-3xs font-bold uppercase tracking-wider px-2 py-1 rounded ${
                                        late.tone === 'overdue'
                                            ? 'bg-danger/10 text-danger'
                                            : late.tone === 'due'
                                              ? 'bg-gold-fill/10 text-gold'
                                              : 'bg-tint/5 text-fg-3'
                                    }`}
                                >
                                    {late.text}
                                </span>
                            </div>

                            {/* The finding / reason */}
                            <div>
                                <p className="text-sm text-fg font-medium">{item.headline}</p>
                                <p className="text-xs text-fg-4 mt-0.5">
                                    {item.detail} · due {fmtDate(item.due_on)}
                                </p>
                            </div>

                            {/* Actions. Email first and full width on a phone —
                                it is the reason this screen exists. */}
                            <div className="flex flex-col sm:flex-row gap-2">
                                <button
                                    onClick={() => {
                                        if (!item.customer_email) {
                                            toast('No email on file for this customer.');
                                            if (item.customer_id) navigate(`/clients/${item.customer_id}`);
                                            return;
                                        }
                                        setEmailing(item);
                                    }}
                                    className="flex-1 min-h-[44px] inline-flex items-center justify-center gap-2 rounded-lg fd-btn-primary font-bold text-sm transition-all px-4"
                                >
                                    <Mail className="w-4 h-4" />
                                    Email
                                </button>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleDismiss(item, 3)}
                                        className="flex-1 sm:flex-none min-h-[44px] inline-flex items-center justify-center gap-1.5 rounded-lg border border-line text-fg-2 hover:text-fg hover:bg-tint/5 text-sm font-semibold transition-colors px-4"
                                    >
                                        <Clock className="w-4 h-4" />
                                        Later
                                    </button>
                                    <button
                                        onClick={() => handleDismiss(item)}
                                        className="flex-1 sm:flex-none min-h-[44px] inline-flex items-center justify-center gap-1.5 rounded-lg border border-line text-fg-2 hover:text-fg hover:bg-tint/5 text-sm font-semibold transition-colors px-4"
                                    >
                                        <Check className="w-4 h-4" />
                                        Done
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                }}
            />

            {emailing && activeOrgId && (
                <CustomerEmailModal
                    isOpen
                    onClose={() => setEmailing(null)}
                    customerEmail={emailing.customer_email ?? ''}
                    customerName={emailing.customer_name}
                    customerId={emailing.customer_id ?? undefined}
                    orgId={activeOrgId}
                    initialTemplateId={LANE_TEMPLATE[lane]}
                    vehicles={
                        emailing.vrm
                            ? [{ id: emailing.vehicle_id ?? undefined, vrm: emailing.vrm }]
                            : []
                    }
                    initialVehicleId={emailing.vehicle_id ?? undefined}
                    onSuccess={() => {
                        // Sent, so it is handled — clear it without a second tap.
                        const sent = emailing;
                        setEmailing(null);
                        void dismiss(sent).then(() => refresh());
                        toast.success('Sent, and cleared from your list');
                    }}
                />
            )}
        </>
    );
}
